/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createContext, useReducer, useRef, useState } from 'react';
import { wizardReducer } from '../reducers/wizard-reducer';
import { WizardActionType } from '../types/wizard-reducer-actions';

type EventHandler = () => any;

const defaultEventHandler = () => {};

const defaultValue = {
  setOnNextEventHandler: (handleEvent: EventHandler) => {},
  setOnBackEventHandler: (handleEvent: EventHandler) => {},
  handleNavigateNext: () => {},
  handleNavigateBack: () => {},
  setBackButtonEnabled: (isEnabled: boolean) => {},
  setNextButtonEnabled: (isEnabled: boolean) => {},
  nextButtonEnabled: false,
  backButtonEnabled: false,
  nextButtonLabel: 'Next',
  backButtonLabel: 'Back',
  nextButtonVisible: false,
  backButtonVisible: false,
  footerVisible: true,
  hideProgressBar: false,
  isLoadingOverlayVisible: false,
  navigateBackEnabled: true,
  setNextButtonLabel: (label: string) => {},
  setBackButtonLabel: (label: string) => {},
  setNextButtonVisible: (isVisible: boolean) => {},
  setBackButtonVisible: (isVisible: boolean) => {},
  setFooterVisible: (isVisible: boolean) => {},
  setHideProgressBar: (isVisible: boolean) => {},
  setLoadingOverlayVisible: (isVisible: boolean) => {},
  setNavigateBackEnabled: (isEnabled: boolean) => {},
  resetToDefaults: () => {},
  setSoftDisable: (isEnabled: boolean) => {},
  softDisable: true,
};

export const WizardContext = createContext(defaultValue);

export function WizardContextProvider({ children }) {
  const [nextButtonEnabled, setNextButtonEnabled] = useState<boolean>(false);
  const [backButtonEnabled, setBackButtonEnabled] = useState<boolean>(true);
  const [nextButtonLabel, setNextButtonLabel] = useState<string>('Next');
  const [backButtonLabel, setBackButtonLabel] = useState<string>('Back');
  const [nextButtonVisible, setNextButtonVisible] = useState<boolean>(true);
  const [backButtonVisible, setBackButtonVisible] = useState<boolean>(true);
  const [footerVisible, setFooterVisible] = useState<boolean>(true);
  const [hideProgressBar, setHideProgressBar] = useState<boolean>(false);
  const [navigateBackEnabled, setNavigateBackEnabled] = useState<boolean>(true);
  const [wizard, dispatch] = useReducer(wizardReducer, {
    isLoadingOverlayVisible: false,
  });
  const [softDisable, setSoftDisable] = useState<boolean>(true);
  const onNextRef = useRef<EventHandler>(defaultEventHandler);
  const onBackRef = useRef<EventHandler>(defaultEventHandler);

  function setOnNextEventHandler(handleEvent: EventHandler) {
    onNextRef.current = handleEvent;
  }

  function setOnBackEventHandler(handleEvent: EventHandler) {
    onBackRef.current = handleEvent;
  }

  function handleNavigateNext() {
    if (onNextRef.current) {
      onNextRef.current();
    }
  }

  function handleNavigateBack() {
    if (onBackRef.current) {
      onBackRef.current();
    }
  }

  function setLoadingOverlayVisible(isVisible: boolean) {
    dispatch({ type: WizardActionType.SET_LOADING_OVERLAY_VISIBLE, isVisible: isVisible });
  }

  function resetToDefaults() {
    setNextButtonEnabled(false);
    setBackButtonEnabled(true);
    setNextButtonLabel('Next');
    setBackButtonLabel('Back');
    setNextButtonVisible(true);
    setBackButtonVisible(true);
    setFooterVisible(true);
    setHideProgressBar(false);
    setNavigateBackEnabled(true);
    setSoftDisable(true);
  }

  return (
    <WizardContext.Provider
      value={{
        setOnNextEventHandler,
        setOnBackEventHandler,
        handleNavigateNext,
        handleNavigateBack,
        setNextButtonEnabled,
        setBackButtonEnabled,
        nextButtonEnabled,
        backButtonEnabled,
        nextButtonLabel,
        backButtonLabel,
        nextButtonVisible,
        backButtonVisible,
        footerVisible,
        hideProgressBar,
        navigateBackEnabled,
        isLoadingOverlayVisible: wizard.isLoadingOverlayVisible,
        setNextButtonLabel,
        setBackButtonLabel,
        setNextButtonVisible,
        setBackButtonVisible,
        setFooterVisible,
        setLoadingOverlayVisible,
        setNavigateBackEnabled,
        setHideProgressBar,
        resetToDefaults,
        setSoftDisable,
        softDisable,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}
